import type { BookingMoveBatch } from "../scheduleOverridesApi"

/** A booking-move batch's status as the pages speak of it. */
export type BookingMoveBatchState = "queued" | "running" | "succeeded" | "failed"

const STATE_BY_NUMBER: readonly BookingMoveBatchState[] = ["queued", "running", "succeeded", "failed"]

/**
 * AmsBookingMoveBatchStatus as a word: the status endpoint sends a number, the notification a name
 * ("Succeeded"). Null when unreadable.
 */
export function decodeBatchStatus(value: number | string | null | undefined): BookingMoveBatchState | null {
  if (typeof value === "number") return STATE_BY_NUMBER[value] ?? null
  if (typeof value !== "string") return null
  if (/^\d$/.test(value)) return decodeBatchStatus(Number(value))
  const lowered = value.toLowerCase() as BookingMoveBatchState
  return STATE_BY_NUMBER.includes(lowered) ? lowered : null
}

/** Whether a batch has stopped for good — nothing more to poll for. */
export function isBatchFinished(state: BookingMoveBatchState | null): boolean {
  return state === "succeeded" || state === "failed"
}

/**
 * One line for a batch's progress or result: "3 of 5 moved, 1 in Booking Review, 1 to go". A batch that
 * failed as a whole says so; the moves it made stand.
 */
export function batchProgressLabel(batch: Pick<BookingMoveBatch, "status" | "totalCount" | "movedCount" | "failedCount" | "pendingCount">): string {
  const parts = [`${batch.movedCount} of ${batch.totalCount} moved`]
  if (batch.failedCount > 0) parts.push(`${batch.failedCount} in Booking Review`)
  if (batch.pendingCount > 0) parts.push(`${batch.pendingCount} to go`)
  const label = parts.join(", ")
  return decodeBatchStatus(batch.status) === "failed" ? `Stopped: ${label}` : label
}
